import { Component } from '@theme/component';

const PDFJS_VERSION = '4.10.38';
const PAGE_FLIP_VERSION = '2.0.7';

const PDFJS_URL = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.min.mjs`;
const PDFJS_WORKER_URL = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;
const PAGE_FLIP_URL = `https://cdn.jsdelivr.net/npm/page-flip@${PAGE_FLIP_VERSION}/dist/js/page-flip.browser.js`;

/** @type {Promise<any> | null} */
let pdfjsPromise = null;
/** @type {Promise<any> | null} */
let pageFlipPromise = null;

/**
 * @returns {Promise<any>}
 */
function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import(PDFJS_URL).then((mod) => {
      const pdfjs = mod.default ?? mod;
      pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

/**
 * @returns {Promise<any>}
 */
function loadPageFlip() {
  if (globalThis.St?.PageFlip) {
    return Promise.resolve(globalThis.St.PageFlip);
  }

  if (!pageFlipPromise) {
    pageFlipPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[data-page-flip="true"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve(globalThis.St.PageFlip), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load page-flip')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = PAGE_FLIP_URL;
      script.async = true;
      script.dataset.pageFlip = 'true';
      script.onload = () => {
        if (globalThis.St?.PageFlip) {
          resolve(globalThis.St.PageFlip);
        } else {
          reject(new Error('page-flip loaded without St.PageFlip'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load page-flip'));
      document.head.appendChild(script);
    });
  }

  return pageFlipPromise;
}

/**
 * PDF flipbook viewer: PDF.js renders pages, StPageFlip handles page turns.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} book
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [pageInfo]
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends {Component<Refs>}
 */
class PdfFlipbook extends Component {
  requiredRefs = ['book'];

  /** @type {any} */
  #pageFlip = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #renderToken = 0;

  /** @type {string[] | null} */
  #images = null;

  /** @type {number} */
  #lastWidth = 0;

  /** @type {any} */
  #PageFlip = null;

  /** @type {MutationObserver | null} */
  #shadowObserver = null;

  /** @type {MediaQueryList | null} */
  #spreadMedia = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#teardown();
  }

  previousPage = () => {
    this.#pageFlip?.flipPrev();
  };

  nextPage = () => {
    this.#pageFlip?.flipNext();
  };

  async #init() {
    const pdfUrl = this.dataset.pdfUrl?.trim();
    if (!pdfUrl) {
      this.#setStatus(this.dataset.emptyLabel || 'Add a PDF URL to display the flipbook.');
      return;
    }

    const token = ++this.#renderToken;
    this.#setStatus(this.dataset.loadingLabel || 'Loading…');
    this.refs.book.replaceChildren();
    this.refs.book.hidden = true;

    try {
      const [pdfjs, PageFlip] = await Promise.all([loadPdfJs(), loadPageFlip()]);
      if (token !== this.#renderToken || this.#abortController?.signal.aborted) return;

      const loadingTask = pdfjs.getDocument({
        url: pdfUrl,
        withCredentials: false,
      });
      const pdf = await loadingTask.promise;
      if (token !== this.#renderToken || this.#abortController?.signal.aborted) return;

      const scale = Number(this.dataset.renderScale || 1.5);
      const images = [];

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        if (token !== this.#renderToken || this.#abortController?.signal.aborted) return;

        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) continue;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          canvas,
          viewport,
        }).promise;
        images.push(canvas.toDataURL('image/jpeg', 0.92));
        this.#setStatus(
          (this.dataset.loadingLabel || 'Loading…') + ` (${pageNumber}/${pdf.numPages})`
        );
      }

      if (!images.length) {
        throw new Error('No pages rendered');
      }

      this.#setStatus('');
      this.refs.book.hidden = false;
      this.#images = images;
      this.#PageFlip = PageFlip;
      this.#mountFlipbook();
      this.#observeResize();
    } catch (error) {
      console.error('[pdf-flipbook]', error);
      this.#setStatus(this.dataset.errorLabel || 'Unable to load this PDF.');
      this.refs.book.hidden = true;
    }
  }

  /**
   * Build HTML page nodes for StPageFlip HTML mode (needed for shadow color control).
   * @returns {HTMLElement[]}
   */
  #buildPages() {
    return (this.#images || []).map((src, index) => {
      const page = document.createElement('div');
      page.className = 'pdf-flipbook__page';
      const img = document.createElement('img');
      img.src = src;
      img.alt = `Page ${index + 1}`;
      img.draggable = false;
      page.appendChild(img);
      return page;
    });
  }

  #mountFlipbook() {
    if (!this.#PageFlip || !this.#images?.length) return;

    this.#teardownFlipbookOnly();

    const book = this.refs.book;
    book.replaceChildren();
    const wrap = book.parentElement;
    const measuredWidth = wrap?.clientWidth || book.clientWidth || this.clientWidth || 0;
    const availableWidth = Math.max(
      measuredWidth || Math.min(window.innerWidth - 32, 600),
      280
    );

    // page_ratio is height / width (A4 ≈ 1.414)
    const ratio = Number(this.dataset.pageRatio || 1.414);
    const preferSpread = this.dataset.usePortrait === 'false';
    const spread = preferSpread && this.#canShowSpread();

    this.dataset.spread = spread ? 'true' : 'false';

    // Spread: each page is half the book. Single: page fills the book width.
    // Keep usePortrait true so StPageFlip collapses to one page when the
    // container is narrower than two pages (mobile).
    const pageWidth = spread ? Math.round(availableWidth / 2) : Math.round(availableWidth);
    const pageHeight = Math.round(pageWidth * ratio);
    this.#lastWidth = availableWidth;

    if (wrap) {
      wrap.style.aspectRatio = spread ? `${pageWidth * 2} / ${pageHeight}` : `1 / ${ratio}`;
    }

    this.#pageFlip = new this.#PageFlip(book, {
      width: pageWidth,
      height: pageHeight,
      size: 'fixed',
      minWidth: pageWidth,
      maxWidth: pageWidth,
      minHeight: pageHeight,
      maxHeight: pageHeight,
      drawShadow: true,
      flippingTime: Number(this.dataset.flipDuration || 700),
      usePortrait: true,
      startPage: 0,
      autoSize: false,
      maxShadowOpacity: 0.45,
      showCover: spread ? this.dataset.showCover === 'true' : false,
      mobileScrollSupport: true,
      useMouseEvents: true,
    });

    this.#pageFlip.loadFromHTML(this.#buildPages());
    this.#pageFlip.on('flip', () => this.#syncControls());
    this.#pageFlip.on('changeState', () => this.#syncControls());
    this.#watchShadowColor();
    this.#syncControls();
  }

  /**
   * StPageFlip hardcodes black shadow gradients; rewrite them to light grey.
   */
  #watchShadowColor() {
    this.#shadowObserver?.disconnect();

    const book = this.refs.book;
    if (!book) return;

    const recolor = () => {
      book
        .querySelectorAll(
          '.stf__outerShadow, .stf__innerShadow, .stf__hardShadow, .stf__hardInnerShadow'
        )
        .forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          const background = el.style.background;
          if (!background || !background.includes('rgba(0, 0, 0')) return;
          el.style.background = background.replace(
            /rgba\(\s*0\s*,\s*0\s*,\s*0\s*,/g,
            'rgba(190, 190, 190,'
          );
        });
    };

    this.#shadowObserver = new MutationObserver(recolor);
    this.#shadowObserver.observe(book, {
      attributes: true,
      subtree: true,
      attributeFilter: ['style'],
      childList: true,
    });
    recolor();
  }

  /**
   * Two-page A4 spread on desktop only; single page on mobile/tablet.
   * @returns {boolean}
   */
  #canShowSpread() {
    if (!this.#spreadMedia) {
      this.#spreadMedia = window.matchMedia('(min-width: 990px)');
    }
    return this.#spreadMedia.matches;
  }

  #observeResize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver?.disconnect();
    let resizeTimer = 0;
    /** @type {boolean | null} */
    let lastSpread = null;

    const remountIfNeeded = (force = false) => {
      if (this.#abortController?.signal.aborted) return;

      const wrap = this.refs.book?.parentElement;
      const nextWidth = Math.max(wrap?.clientWidth || this.clientWidth || 0, 0);
      const nextSpread = this.dataset.usePortrait === 'false' && this.#canShowSpread();
      const spreadChanged = lastSpread !== null && lastSpread !== nextSpread;
      lastSpread = nextSpread;

      if (!force && !spreadChanged) {
        if (!nextWidth || Math.abs(nextWidth - this.#lastWidth) < 8) return;
      }

      this.#mountFlipbook();
    };

    this.#resizeObserver = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => remountIfNeeded(false), 200);
    });

    this.#resizeObserver.observe(this);

    if (!this.#spreadMedia) {
      this.#spreadMedia = window.matchMedia('(min-width: 990px)');
    }
    this.#spreadMedia.addEventListener('change', () => {
      if (this.#images?.length) remountIfNeeded(true);
    });
  }

  #syncControls() {
    if (!this.#pageFlip) return;

    const current = this.#pageFlip.getCurrentPageIndex() + 1;
    const total = this.#pageFlip.getPageCount();
    const pageInfo = this.refs.pageInfo;

    if (pageInfo) {
      const template = this.dataset.pageLabel || 'Page {{ current }} of {{ total }}';
      pageInfo.textContent = template
        .replace('__CURRENT__', String(current))
        .replace('__TOTAL__', String(total));
    }

    if (this.refs.prevButton) {
      this.refs.prevButton.disabled = current <= 1;
    }
    if (this.refs.nextButton) {
      this.refs.nextButton.disabled = current >= total;
    }
  }

  /**
   * @param {string} message
   */
  #setStatus(message) {
    const status = this.refs.status;
    if (!status) return;

    status.textContent = message;
    status.hidden = message === '';
  }

  #teardownFlipbookOnly() {
    this.#shadowObserver?.disconnect();
    this.#shadowObserver = null;

    if (this.#pageFlip) {
      try {
        this.#pageFlip.destroy();
      } catch {
        // Ignore teardown errors from page-flip
      }
      this.#pageFlip = null;
    }
  }

  #teardown() {
    this.#renderToken += 1;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#teardownFlipbookOnly();
    this.#images = null;
    this.#PageFlip = null;
    this.#lastWidth = 0;
  }
}

if (!customElements.get('pdf-flipbook-component')) {
  customElements.define('pdf-flipbook-component', PdfFlipbook);
}
